export function containsStructuralBlocks(el) {
  return !!el?.querySelector?.(
    'h1,h2,h3,h4,h5,h6,p,ul,ol,table,blockquote,pre,hr'
  );
}

export function findFormatBlockTargetFrom(node, root) {
  if (!node) return null;
  let el = node;
  while (el && el !== root) {
    if (/^H[1-6]$|^P$/i.test(el.nodeName)) return el;
    el = el.parentElement;
  }
  el = node;
  while (el && el !== root) {
    if (el.nodeName === 'DIV' && !containsStructuralBlocks(el)) return el;
    el = el.parentElement;
  }
  return null;
}

/** Block tag for the format dropdown, or '' (lists / loose wrapper text → no format). */
export function getToolbarBlockFormatValue(anchorNode, root) {
  if (!anchorNode || !root?.contains?.(anchorNode)) return '';
  const el =
    anchorNode.nodeType === Node.ELEMENT_NODE
      ? anchorNode
      : anchorNode.parentElement;
  if (!el) return '';
  if (el.closest?.('ul,ol,li')) return '';

  let n = el;
  while (n && n !== root) {
    const name = n.nodeName;
    if (/^H[1-6]$|^P$/i.test(name)) return name.toLowerCase();
    n = n.parentElement;
  }
  return '';
}

/**
 * Unwrap nearest h1–h6 / p from caret (matches format dropdown semantics).
 * Returns true if a block was unwrapped.
 */
export function unwrapBlockFormatAtCaret(sel, root) {
  if (!sel?.anchorNode || !root.contains(sel.anchorNode)) return false;
  const startEl =
    sel.anchorNode.nodeType === Node.ELEMENT_NODE
      ? sel.anchorNode
      : sel.anchorNode.parentElement;
  const block = findFormatBlockTargetFrom(startEl, root);
  if (!block || !/^H[1-6]|P$/i.test(block.nodeName)) return false;

  const parent = block.parentNode;
  if (!parent) return false;

  let first = null;
  while (block.firstChild) {
    if (!first) first = block.firstChild;
    parent.insertBefore(block.firstChild, block);
  }
  parent.removeChild(block);
  parent.normalize();

  if (first) {
    const r = document.createRange();
    const start =
      first.nodeType === Node.TEXT_NODE ? first : first.firstChild || first;
    if (start.nodeType === Node.TEXT_NODE) {
      r.setStart(start, 0);
    } else {
      r.setStartBefore(first);
    }
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  }
  return true;
}

export function placeCaretAtEndOfBlock(block, sel) {
  const newRange = document.createRange();
  newRange.selectNodeContents(block);
  newRange.collapse(false);
  sel.removeAllRanges();
  sel.addRange(newRange);
}

export function wrapRangeInBlockTag(range, tagName, sel) {
  const el = document.createElement(tagName.toLowerCase());
  try {
    range.surroundContents(el);
  } catch {
    const contents = range.extractContents();
    el.appendChild(contents);
    range.insertNode(el);
  }
  placeCaretAtEndOfBlock(el, sel);
  return el;
}

/** Collapsed caret: adjacent TEXT_NODE siblings until BR / non-text. */
export function expandRangeToSiblingTextRunBetweenBr(range) {
  if (!range || !range.collapsed) return range;

  let container = range.startContainer;
  if (container.nodeType === Node.ELEMENT_NODE) {
    const child =
      container.childNodes[range.startOffset] ||
      container.childNodes[range.startOffset - 1];
    if (child?.nodeType === Node.TEXT_NODE) {
      container = child;
    }
  }
  if (container.nodeType !== Node.TEXT_NODE) return range;

  let first = container;
  while (first.previousSibling) {
    const prev = first.previousSibling;
    if (prev.nodeName === 'BR') break;
    if (prev.nodeType !== Node.TEXT_NODE) break;
    first = prev;
  }

  let last = container;
  while (last.nextSibling) {
    const next = last.nextSibling;
    if (next.nodeName === 'BR') break;
    if (next.nodeType !== Node.TEXT_NODE) break;
    last = next;
  }

  const r = document.createRange();
  r.setStart(first, 0);
  r.setEnd(last, (last.textContent || '').length);
  return r;
}

export function expandRangeToWord(range) {
  if (!range) return range;

  if (!range.collapsed && range.toString().trim()) return range;

  let container = range.startContainer;
  if (!container) return range;

  if (container.nodeType === Node.ELEMENT_NODE) {
    const child =
      container.childNodes[range.startOffset] ||
      container.childNodes[container.childNodes.length - 1];
    if (child?.nodeType === Node.TEXT_NODE) {
      container = child;
    }
  }

  if (container.nodeType !== Node.TEXT_NODE) return range;

  const text = container.textContent || '';
  let start = range.startOffset;
  let end = range.startOffset;

  const isWordChar = (c) => /[A-Za-z0-9_]/.test(c);

  while (start > 0 && isWordChar(text[start - 1])) start--;
  while (end < text.length && isWordChar(text[end])) end++;

  if (start === end) return range;

  const r = range.cloneRange();
  r.setStart(container, start);
  r.setEnd(container, end);
  return r;
}

export function cleanPaste(html) {
  const parser = new DOMParser();
  const dom = parser.parseFromString(html, 'text/html');
  [...dom.body.children].forEach((c) => {
    if (c.tagName === 'B' && c.id.startsWith('docs-internal-guid')) {
      c.outerHTML = c.innerHTML;
    }
  });
  let out = dom.body.innerHTML;

  out = out.replace(/<(\/)*(\\?xml:|meta|link|span|font|del|ins|st1:|[ovwxp]:)((.|\s)*?)>/gi, '');
  out = out.replace(/ (id|class|style|type|start|dir|aria-level|role)=("(.*?)"|(\w*))/gi, '');
  out = out.replace(/<style(.*?)style>/gi, '');
  out = out.replace(/<script(.*?)script>/gi, '');
  out = out.replace(/<!--(.*?)-->/gi, '');

  return out;
}

export function recursivelyCleanUpContentEditableHtml(node) {
  if (!node) return;
  node.childNodes.forEach(recursivelyCleanUpContentEditableHtml);
  if (node.nodeName === 'A' && node.textContent.trim() === '' && node.children.length === 0) {
    node.remove();
    return;
  }
  if (
    ['H1', 'H2', 'H3', 'H4', 'H5', 'P', 'SPAN', 'B', 'I', 'U', 'STRIKE'].includes(node.nodeName) &&
    node.childNodes.length === 0 &&
    !node.className
  ) {
    node?.remove();
    return;
  }
  if (!node.parentNode) return;
  if (!node.parentNode.parentNode) return;
  if (
    node.parentNode.parentNode.childNodes.length === 2 &&
    node.parentNode.parentNode.childNodes[1].nodeName === 'BR'
  ) {
    node.parentNode.parentNode.replaceChildren(node.parentNode);
  }
  if (['SPAN'].includes(node.parentNode.nodeName)) {
    node.parentNode.parentNode.replaceChild(node, node.parentNode);
  } else if (['B', 'I', 'U'].includes(node.parentNode.nodeName)) {
    node.parentNode.removeAttribute('style');
  }
}

export function recursivelyTraverseIfThereIsOnlyChild(node) {
  if (node.childNodes.length === 0 && node.nodeName === 'BR') {
    return true;
  }
  if (node.childNodes.length !== 1) {
    return false;
  }
  return recursivelyTraverseIfThereIsOnlyChild(node.childNodes[0]);
}

export function isCaretAtLogicalEnd(range, block) {
  if (
    range.startContainer === block &&
    range.startOffset === block.childNodes.length
  ) {
    return true;
  }

  if (range.startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = range.startContainer;

    const beforeCaret = textNode.textContent.slice(0, range.startOffset);

    if (beforeCaret.trim().length < textNode.textContent.trim().length) {
      return false;
    }

    let n = textNode.nextSibling;
    while (n) {
      if (n.nodeType === Node.TEXT_NODE && n.textContent.trim()) return false;
      if (n.nodeType === Node.ELEMENT_NODE && n.nodeName !== 'BR') return false;
      n = n.nextSibling;
    }

    return true;
  }

  return false;
}

export function normalizeAndGetRange(root) {
  root.normalize();

  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;

  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;

  return range;
}

export function shouldLetBrowserHandleEnter(range) {
  const node =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer.parentElement;

  return !!node?.closest(
    'ul,ol,li,table,tbody,thead,tr,td,th,pre,code,blockquote,figure,figcaption,hr,details,summary,textarea'
  );
}

export function getSelectedBlocks(range, root) {
  const blocks = new Set();

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode(node) {
        if (!node.matches?.('h1,h2,h3,h4,h5,p,div')) {
          return NodeFilter.FILTER_SKIP;
        }
        if (node === root) return NodeFilter.FILTER_SKIP;
        return range.intersectsNode(node)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    }
  );

  while (walker.nextNode()) {
    blocks.add(walker.currentNode);
  }

  return [...blocks];
}

export function getCurrentLinkFromRange(range) {
  if (!range) return null;

  let node =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer.parentElement;

  return node?.closest?.('a') || null;
}

export function rangeIntersectsLink(range) {
  if (!range) return false;
  const fragment = range.cloneContents();
  return !!fragment.querySelector?.('a');
}

export function getTableCellFromSelection(sel, root) {
  if (!sel?.anchorNode || !root?.contains?.(sel.anchorNode)) return null;
  const n = sel.anchorNode;
  const el = n.nodeType === Node.ELEMENT_NODE ? n : n.parentElement;
  const cell = el?.closest?.('td,th');
  if (!cell || !root.contains(cell)) return null;
  return cell;
}

/** Equal-width columns: fixed layout + colgroup (re-run after column add/remove). */
function applyEqualColumnWidths(table) {
  if (!table || !table.rows?.length) return;
  let n = 0;
  for (let i = 0; i < table.rows.length; i += 1) {
    n = Math.max(n, table.rows[i].cells.length);
  }
  if (n < 1) return;
  table.style.tableLayout = 'fixed';
  if (!table.style.width) table.style.width = '100%';
  table.querySelectorAll('colgroup').forEach((cg) => cg.remove());
  const colgroup = document.createElement('colgroup');
  const pct = 100 / n;
  const w = `${Number.isInteger(pct) ? pct : Number(pct.toFixed(4))}%`;
  for (let i = 0; i < n; i += 1) {
    const col = document.createElement('col');
    col.style.width = w;
    colgroup.appendChild(col);
  }
  let anchor = [...table.children].find((c) =>
    /^(THEAD|TBODY|TFOOT)$/i.test(c.tagName)
  );
  if (!anchor && table.rows.length) {
    const tr0 = table.rows[0];
    anchor = tr0.parentNode === table ? tr0 : tr0.parentNode;
  }
  if (anchor) table.insertBefore(colgroup, anchor);
  else table.appendChild(colgroup);
}

export function createTableElement(rows, cols) {
  const table = document.createElement('table');
  table.style.borderCollapse = 'collapse';
  table.style.width = '100%';
  table.setAttribute('border', '1');
  const tbody = document.createElement('tbody');
  for (let r = 0; r < rows; r += 1) {
    const tr = document.createElement('tr');
    for (let c = 0; c < cols; c += 1) {
      const td = document.createElement('td');
      td.appendChild(document.createElement('br'));
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  applyEqualColumnWidths(table);
  return table;
}

function makeEmptyRow(colCount) {
  const newTr = document.createElement('tr');
  for (let i = 0; i < colCount; i += 1) {
    const td = document.createElement('td');
    td.appendChild(document.createElement('br'));
    newTr.appendChild(td);
  }
  return newTr;
}

export function addTableRowBefore(cell) {
  const tr = cell.closest('tr');
  if (!tr?.parentElement) return;
  const newTr = makeEmptyRow(tr.children.length);
  tr.parentNode.insertBefore(newTr, tr);
}

export function addTableRowAfter(cell) {
  const tr = cell.closest('tr');
  if (!tr?.parentElement) return;
  const newTr = makeEmptyRow(tr.children.length);
  tr.parentNode.insertBefore(newTr, tr.nextSibling);
}

function insertColumnAtIndex(table, idx, beforeRef) {
  table.querySelectorAll('tr').forEach((row) => {
    const ref = row.children[idx];
    if (!ref) return;
    const td = document.createElement('td');
    td.appendChild(document.createElement('br'));
    if (beforeRef) {
      row.insertBefore(td, ref);
    } else {
      ref.after(td);
    }
  });
  applyEqualColumnWidths(table);
}

export function addTableColumnBefore(cell) {
  const table = cell.closest('table');
  const tr = cell.closest('tr');
  if (!table || !tr) return;
  const idx = Array.prototype.indexOf.call(tr.children, cell);
  if (idx < 0) return;
  insertColumnAtIndex(table, idx, true);
}

export function addTableColumnAfter(cell) {
  const table = cell.closest('table');
  const tr = cell.closest('tr');
  if (!table || !tr) return;
  const idx = Array.prototype.indexOf.call(tr.children, cell);
  if (idx < 0) return;
  insertColumnAtIndex(table, idx, false);
}

export function removeTableRow(cell) {
  const tr = cell.closest('tr');
  const section = tr?.parentElement;
  if (!tr || !section) return;
  if (section.querySelectorAll('tr').length <= 1) return;
  tr.remove();
}

export function removeTableColumn(cell) {
  const table = cell.closest('table');
  const tr = cell.closest('tr');
  if (!table || !tr) return;
  const idx = Array.prototype.indexOf.call(tr.children, cell);
  if (idx < 0) return;
  if (tr.children.length <= 1) return;
  table.querySelectorAll('tr').forEach((row) => {
    row.children[idx]?.remove();
  });
  applyEqualColumnWidths(table);
}

export function removeEntireTable(cell) {
  cell.closest('table')?.remove();
}
